<template>
  <footer class="footer" role="contentinfo">
    <div class="footer-card">
      <div class="footer-grid">
        <div class="brand-column">
          <div class="brand-lockup">
            <!-- Decorative: the wordmark beside it already says the name, so an
                 alt here would announce "ArticuLearn" twice. -->
            <img
              src="@/assets/articulearn-logo-a.png"
              alt=""
              class="brand-mark"
            />
            <span class="brand-name u-display">ArticuLearn</span>
          </div>
          <p class="brand-tagline">{{ $t("home.footer.tagline") }}</p>
          <StoreBadges />
        </div>

        <div class="links-column">
          <h2 class="footer-heading">
            {{ $t("home.footer.quickLinksHeading") }}
          </h2>
          <!-- Three rows filled column-first, so the reading order down the
               left sub-column is About Us / FAQ / Privacy Policy and the right
               one is Terms of Service / Have feedback?. -->
          <div class="quick-links">
            <router-link to="/about-us" class="footer-link">
              {{ $t("about.title") }}
            </router-link>
            <router-link to="/faq" class="footer-link">
              {{ $t("navigation.faq") }}
            </router-link>
            <router-link to="/privacy-policy" class="footer-link">
              {{ $t("navigation.privacyPolicy") }}
            </router-link>
            <router-link to="/terms-of-service" class="footer-link">
              {{ $t("navigation.termsOfService") }}
            </router-link>
            <button
              type="button"
              class="footer-link feedback-link"
              @click="showFeedback = true"
            >
              {{ $t("home.footer.feedbackLink") }}
            </button>
          </div>

          <h2 class="footer-heading find-us-heading">
            {{ $t("home.footer.findUsHeading") }}
          </h2>
          <!-- The three outbound chips are written out rather than looped on
               purpose. The call-site scan in scripts/localeCoverage.js (rule 7)
               only sees a translation call whose first argument is a quoted
               literal, so a key read from a data array would leave these three
               keys with no checked call site, and a typo would then render the
               raw dotted key as the chip's accessible name in every locale
               with CI green.

               Glyph paths are copied from Font Awesome Free 6
               (brands/instagram, brands/facebook-f, brands/tiktok and
               solid/newspaper):
               Font Awesome Free 6.5.2 by @fontawesome - https://fontawesome.com
               License: https://fontawesome.com/license/free (Icons: CC BY 4.0)
               -->
          <div class="social-chips">
            <a
              :href="instagramUrl"
              class="social-chip"
              target="_blank"
              rel="noopener"
              :aria-label="$t('navigation.instagramAlt')"
            >
              <svg
                class="chip-glyph"
                viewBox="0 0 448 512"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"
                />
              </svg>
              <span class="chip-label">Instagram</span>
            </a>
            <a
              :href="facebookUrl"
              class="social-chip"
              target="_blank"
              rel="noopener"
              :aria-label="$t('navigation.facebookAlt')"
            >
              <svg
                class="chip-glyph"
                viewBox="0 0 320 512"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M80 299.3V512H196V299.3h86.5l18-97.8H196V166.9c0-51.7 20.3-71.5 72.7-71.5c16.3 0 29.4 .4 37 1.2V7.9C291.4 4 256.4 0 236.2 0C129.3 0 80 50.5 80 159.4v42.1H14v97.8H80z"
                />
              </svg>
              <span class="chip-label">Facebook</span>
            </a>
            <a
              :href="tiktokUrl"
              class="social-chip social-chip-featured"
              target="_blank"
              rel="noopener"
              :aria-label="$t('navigation.tiktokAlt')"
            >
              <svg
                class="chip-glyph"
                viewBox="0 0 448 512"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"
                />
              </svg>
              <span class="chip-label">TikTok</span>
            </a>
            <router-link to="/blog" class="social-chip">
              <svg
                class="chip-glyph"
                viewBox="0 0 512 512"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M96 96c0-35.3 28.7-64 64-64H448c35.3 0 64 28.7 64 64V416c0 35.3-28.7 64-64 64H80c-44.2 0-80-35.8-80-80V128c0-17.7 14.3-32 32-32s32 14.3 32 32V400c0 8.8 7.2 16 16 16s16-7.2 16-16V96zm64 24v80c0 13.3 10.7 24 24 24H296c13.3 0 24-10.7 24-24V120c0-13.3-10.7-24-24-24H184c-13.3 0-24 10.7-24 24zm208-8c0 8.8 7.2 16 16 16h48c8.8 0 16-7.2 16-16s-7.2-16-16-16H384c-8.8 0-16 7.2-16 16zm0 96c0 8.8 7.2 16 16 16h48c8.8 0 16-7.2 16-16s-7.2-16-16-16H384c-8.8 0-16 7.2-16 16zM160 304c0 8.8 7.2 16 16 16H432c8.8 0 16-7.2 16-16s-7.2-16-16-16H176c-8.8 0-16 7.2-16 16zm0 96c0 8.8 7.2 16 16 16H432c8.8 0 16-7.2 16-16s-7.2-16-16-16H176c-8.8 0-16 7.2-16 16z"
                />
              </svg>
              <span class="chip-label">{{ $t("navigation.blogs") }}</span>
            </router-link>
          </div>
        </div>
      </div>

      <hr class="footer-divider" />

      <div class="footer-bottom">
        <p class="copyright">&copy; ArticuLearn {{ currentYear }}</p>
        <p class="made-with">{{ $t("home.footer.madeWith") }}</p>
      </div>
    </div>
    <FeedbackModal v-model="showFeedback" />
  </footer>
</template>

<script>
import StoreBadges from "@/components/StoreBadges.vue";
import FeedbackModal from "@/components/FeedbackModal.vue";
import {
  INSTAGRAM_URL,
  FACEBOOK_URL,
  TIKTOK_URL,
} from "@/constants/socialLinks.js";

export default {
  name: "FooterSection",
  components: { StoreBadges, FeedbackModal },
  data() {
    return {
      // The feedback form used to occupy a full section of the landing page.
      // It is a support channel rather than a conversion step, so it now opens
      // on demand from here, on every page the footer renders on.
      showFeedback: false,
      instagramUrl: INSTAGRAM_URL,
      facebookUrl: FACEBOOK_URL,
      tiktokUrl: TIKTOK_URL,
    };
  },
  computed: {
    // Read at render rather than typed into the markup: the notice said 2026
    // because someone typed 2026, and it is also the only way "ArticuLearn"
    // is left as the sole word in that text node, which is what keeps a
    // proper noun out of the locale bundles.
    currentYear() {
      return new Date().getFullYear();
    },
  },
};
</script>

<style scoped>
/* The band behind the card. --al-surface-page is the tinted page background
   documented for exactly this, and the 32px / 16px gutter is the DESIGN.md
   screen-padding rule, the same shape HeroSection's .hero-inner uses. */
.footer {
  background-color: var(--al-surface-page);
  width: 100%;
  padding: var(--al-space-xl);
}

.footer-card {
  max-width: 1120px;
  margin: 0 auto;
  padding: var(--al-space-2xl);
  background-color: var(--al-surface-raised);
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius-lg);
  box-shadow: var(--al-shadow-sm);
}

.footer-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  column-gap: var(--al-space-2xl);
}

/* A gapped stack rather than a fixed layout: StoreBadges renders nothing in
   the native shells and one badge when IOS_LIVE is false, so the column has to
   simply end at the tagline in those cases rather than reserve dead space. */
.brand-column {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--al-space-md);
}

.brand-lockup {
  display: flex;
  align-items: center;
  gap: var(--al-space-sm);
}

/* width: auto rather than a square box: the source bitmap is portrait
   (212 by 284), so a 44px square would squash it. */
.brand-mark {
  height: 44px;
  width: auto;
  display: block;
}

.brand-name {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--al-text);
}

.brand-tagline {
  margin: 0;
  max-width: 34ch;
  font-size: 16px;
  color: var(--al-text-muted);
}

.brand-column :deep(.store-badges) {
  align-items: flex-start;
}

.brand-column :deep(.store-badge) {
  height: 40px;
}

.links-column {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

/* The DESIGN.md Caption style. The headings are stored sentence-case in the
   bundles and uppercased here, so locales where all-caps is wrong or
   meaningless still read naturally in the file. */
.footer-heading {
  margin: 0 0 var(--al-space-sm);
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--al-text);
}

.find-us-heading {
  margin-top: var(--al-space-lg);
}

.quick-links {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(3, auto);
  grid-auto-flow: column;
  column-gap: var(--al-space-md);
  row-gap: var(--al-space-sm);
}

.footer-link {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: var(--al-space-xs) 0;
  color: var(--al-text-muted);
  font-size: 16px;
  text-decoration: none;
  /* The two sub-columns are narrowest just above the 767px breakpoint: about
     121px each at a 768px viewport. Eight bundles hold a single unbreakable
     label word longer than that (German "Datenschutzerklaerung" at 20
     characters is the worst), which would otherwise paint across the
     neighbouring sub-column and past the card's padding edge. `anywhere` and
     not `break-word`: this row is a flex container, so the label is an
     anonymous flex item sized from its min-content width, and only `anywhere`
     lowers that width. Measured at 768px, `break-word` still overflowed by
     43px. */
  overflow-wrap: anywhere;
  transition: color var(--al-dur-short) var(--al-ease-move);
}

/* An in-page action, so it is a real <button>, not an <a href="#"> that would
   push a history entry. Stripped back to read as one of the link column's
   siblings; font-family and line-height do not inherit into a button. */
.feedback-link {
  background: none;
  border: none;
  font-family: inherit;
  line-height: inherit;
  text-align: left;
  cursor: pointer;
}

.social-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--al-space-sm);
}

/* 44px is the DESIGN.md touch floor, and --al-space-md is the 16px of side
   padding the design fixes. The neutral fill plus hairline is deliberately
   quiet: --al-text-muted on --al-surface-sunken measures 4.9:1, and the label
   in --al-text is far above that, so the resting chip stays legible. */
.social-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--al-space-sm);
  min-height: 44px;
  padding: 0 var(--al-space-md);
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius-pill);
  background-color: var(--al-surface-sunken);
  color: var(--al-text);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: background-color var(--al-dur-short) var(--al-ease-move),
    transform var(--al-dur-micro) var(--al-ease-move);
}

.chip-glyph {
  height: 18px;
  width: auto;
  flex: none;
  color: var(--al-text-muted);
  fill: currentColor;
}

/* The featured chip's label stays --al-text rather than --al-text-link: the
   link hue measures 3.80:1 on --al-primary-soft and 3.34:1 on
   --al-primary-soft-hover, both under the 4.5:1 that 14px text needs. The
   glyph is non-text, where the threshold is 3:1, so it carries the link hue at
   those same ratios. In dark mode the pair measures 3.70:1 and 3.04:1. */
.social-chip-featured {
  background-color: var(--al-primary-soft);
  border-color: color-mix(in srgb, var(--al-primary) 35%, transparent);
}

.social-chip-featured .chip-glyph {
  color: var(--al-text-link);
}

/* One ring for every focusable thing in the footer, the same one the feedback
   button already drew. No border-radius here: the outline follows each
   element's own corner, which keeps the pill chips pill-shaped. */
.footer-link:focus-visible,
.social-chip:focus-visible {
  outline: 2px solid var(--al-primary-hover);
  outline-offset: 2px;
}

.footer-divider {
  margin: var(--al-space-xl) 0 var(--al-space-lg);
  border: none;
  border-top: 1px solid var(--al-border);
}

.footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--al-space-sm);
}

.copyright,
.made-with {
  margin: 0;
  font-size: 14px;
  color: var(--al-text-muted);
}

/* On a touch WebView :hover latches onto the last-tapped element until the
   next tap elsewhere, so every hover rule in this file sits behind the
   capability guard and the press feedback below stays outside it. */
@media (hover: hover) and (pointer: fine) {
  .footer-link:hover {
    color: var(--al-text);
  }

  .social-chip:hover {
    background-color: var(--al-primary-soft);
  }

  .social-chip-featured:hover {
    background-color: var(--al-primary-soft-hover);
  }
}

/* Touch gets no hover, so the press scale is the chip's only tap affordance
   and stays outside the capability guard. */
.social-chip:active {
  transform: scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .social-chip {
    transition: none;
  }

  .social-chip:active {
    transform: none;
  }
}

@media (max-width: 767px) {
  .footer {
    padding: var(--al-space-md);
  }

  .footer-card {
    padding: var(--al-space-lg);
  }

  .footer-grid {
    grid-template-columns: minmax(0, 1fr);
    row-gap: var(--al-space-xl);
  }

  /* The two sub-columns do not survive here: the card's inner width is about
     295px at a 375px viewport, and a label such as the French
     "Politique de confidentialite" would wrap inside a 140px half. */
  .quick-links {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
    grid-auto-flow: row;
  }

  .footer-bottom {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
